import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import { InspectionService, type InspectionsListResponse } from '../../services/inspections';
import { DamageService } from '../../services/damages';
import type { GridFilters } from '../../types/common/dashboard';
import type { ApiError } from '../../types/api/network';
import type { VinInspectionsResponse, InspectionDamage } from './inspectionTypes';
import type { InspectionAssetsResponse } from '../../types/common/asset';
import { normalizeDamage } from '../../utils/damageGrouping';
import { acquireInspectionPlayCookieRefresh } from '../../utils/inspectionPlayCookieRefresh';
import { features } from '../../config/platform';

export type { InspectionsListResponse } from '../../services/inspections';

const refreshInspectionPlayCookiesBestEffort = async (inspectionId: string) => {
  // Signed play cookies are a cloud-portal concept; on-prem deployments
  // don't need them refreshed.
  if (!features.isCloudPortal) {
    return;
  }

  try {
    await InspectionService.refreshInspectionPlayCookies(inspectionId);
  } catch (error) {
    console.error('Failed to refresh inspection play cookies', error);
  }
};

export const inspectionApi = createApi({
  reducerPath: 'inspectionApi',
  baseQuery: fakeBaseQuery<ApiError>(),
  keepUnusedDataFor: 300,
  tagTypes: ['VinInspections', 'InspectionDamages', 'InspectionImageDamages', 'InspectionAssets'],
  endpoints: builder => ({
    getInspections: builder.query<InspectionsListResponse, GridFilters>({
      queryFn: async (params, { signal }) => {
        try {
          const response = await InspectionService.getInspections(params, false, signal);
          return { data: response.data as InspectionsListResponse };
        } catch (error) {
          return { error: error as ApiError };
        }
      },
      providesTags: [{ type: 'VinInspections', id: 'LIST' }],
    }),
    getInspectionsByVin: builder.query<VinInspectionsResponse, string>({
      queryFn: async (vin, { signal }) => {
        try {
          const response = await InspectionService.getInspectionsByVin(vin, signal);
          return { data: response.data as VinInspectionsResponse };
        } catch (error) {
          return { error: error as ApiError };
        }
      },
      providesTags: (_result, _error, vin) => [
        { type: 'VinInspections', id: vin },
        { type: 'VinInspections', id: 'LIST' },
      ],
    }),
    getDamages: builder.query<InspectionDamage[], string>({
      queryFn: async (inspectionId, { signal }) => {
        try {
          await refreshInspectionPlayCookiesBestEffort(inspectionId);
          const response: any = await DamageService.getDamages(inspectionId, signal);
          const damageList = response?.data?.inspection?.damages || [];
          return { data: damageList.map((item: InspectionDamage) => normalizeDamage(item)) };
        } catch (error) {
          return { error: error as ApiError };
        }
      },
      providesTags: (_result, _error, inspectionId) => [{ type: 'InspectionDamages', id: inspectionId }],
      onCacheEntryAdded: async (inspectionId, { cacheEntryRemoved }) => {
        if (!inspectionId) {
          return;
        }

        // Shares a single refresh timer per inspectionId with any other
        // consumer (e.g. the assets tab) instead of running an independent one.
        const release = acquireInspectionPlayCookieRefresh(inspectionId);

        await cacheEntryRemoved;
        release();
      },
    }),
    getInspectionAssets: builder.query<InspectionAssetsResponse, string>({
      queryFn: async (inspectionId, { signal }) => {
        try {
          const response = await InspectionService.getInspectionAssets(inspectionId, signal);
          return { data: (response?.data as InspectionAssetsResponse) || { items: [] } };
        } catch (error) {
          return { error: error as ApiError };
        }
      },
      providesTags: (_result, _error, inspectionId) => [{ type: 'InspectionAssets', id: inspectionId }],
      onCacheEntryAdded: async (inspectionId, { cacheDataLoaded, cacheEntryRemoved }) => {
        if (!inspectionId) {
          return;
        }

        try {
          // Match prior assets-hook behavior: only keep play cookies alive after
          // a successful assets load (not while the request is in flight / failed).
          await cacheDataLoaded;
        } catch {
          await cacheEntryRemoved;
          return;
        }

        const release = acquireInspectionPlayCookieRefresh(inspectionId);
        await cacheEntryRemoved;
        release();
      },
    }),
    getDamagesByImageUri: builder.query<InspectionDamage[], { inspectionId: string; imageURI: string }>({
      queryFn: async ({ inspectionId, imageURI }, { signal }) => {
        try {
          const response: any = await DamageService.getDamagesByImageUri(inspectionId, imageURI, signal);
          const damageList =
            response?.data?.inspection?.damages ?? response?.data?.damages ?? response?.data?.damage ?? response?.data ?? [];
          const normalizedDamageList = Array.isArray(damageList) ? damageList : [damageList].filter(Boolean);

          return {
            data: normalizedDamageList.map((item: InspectionDamage) => normalizeDamage(item)),
          };
        } catch (error) {
          return { error: error as ApiError };
        }
      },
      providesTags: (_result, _error, { inspectionId, imageURI }) => [
        { type: 'InspectionImageDamages', id: `${inspectionId}:${imageURI}` },
      ],
    }),
    createInspectionDamage: builder.mutation<unknown, { inspectionId: string; payload?: Record<string, unknown> }>({
      queryFn: async ({ inspectionId, payload }, { signal }) => {
        try {
          const response = await DamageService.createInspectionDamage(inspectionId, payload, signal);
          return { data: response.data };
        } catch (error) {
          return { error: error as ApiError };
        }
      },
      invalidatesTags: (_result, _error, { inspectionId }) => [
        { type: 'InspectionDamages', id: inspectionId },
        { type: 'InspectionImageDamages' },
        { type: 'VinInspections', id: 'LIST' },
      ],
    }),
    approveInspectionDamage: builder.mutation<unknown, { inspectionId: string; damageId?: string; payload?: Record<string, unknown> }>({
      queryFn: async ({ inspectionId, damageId, payload }, { signal }) => {
        try {
          const response = await DamageService.approveInspectionDamage(inspectionId, damageId, payload, signal);
          return { data: response.data };
        } catch (error) {
          return { error: error as ApiError };
        }
      },
      invalidatesTags: (_result, _error, { inspectionId }) => [
        { type: 'InspectionDamages', id: inspectionId },
        { type: 'InspectionImageDamages' },
        { type: 'VinInspections', id: 'LIST' },
      ],
    }),
    updateInspectionDamage: builder.mutation<unknown, { inspectionId: string; damageId: string; payload?: Record<string, unknown> }>({
      queryFn: async ({ inspectionId, damageId, payload }, { signal }) => {
        try {
          const response = await DamageService.updateInspectionDamage(inspectionId, damageId, payload, signal);
          return { data: response.data };
        } catch (error) {
          return { error: error as ApiError };
        }
      },
      invalidatesTags: (_result, _error, { inspectionId }) => [
        { type: 'InspectionDamages', id: inspectionId },
        { type: 'InspectionImageDamages' },
        { type: 'VinInspections', id: 'LIST' },
      ],
    }),
    deleteInspectionDamage: builder.mutation<unknown, { inspectionId: string; damageId?: string; payload?: Record<string, unknown> }>({
      queryFn: async ({ inspectionId, damageId, payload }, { signal }) => {
        try {
          const response = await DamageService.deleteInspectionDamage(inspectionId, damageId, payload, signal);
          return { data: response.data };
        } catch (error) {
          return { error: error as ApiError };
        }
      },
      invalidatesTags: (_result, _error, { inspectionId }) => [
        { type: 'InspectionDamages', id: inspectionId },
        { type: 'InspectionImageDamages' },
        { type: 'VinInspections', id: 'LIST' },
      ],
    }),
    exportInspections: builder.mutation<Blob, GridFilters>({
      queryFn: async (params, { signal }) => {
        try {
          const response = await InspectionService.getInspections(params, true, signal);
          return { data: response.data as Blob };
        } catch (error) {
          return { error: error as ApiError };
        }
      },
    }),
  }),
});
